(() => {
  const ROOT_SELECTOR = '[data-farmea-product]';
  const AURA_SELECTOR = '[data-aura-counter]';
  const auraStates = new WeakMap();
  let auraObserver = null;

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function parseAura(rawValue) {
    const raw = String(rawValue ?? '')
      .replace(/\s*AURA\s*$/i, '')
      .trim();

    if (!raw) return { type: 'text', raw: '' };

    if (raw.includes('∞')) {
      return {
        type: 'infinity',
        raw,
        prefix: raw.startsWith('+') ? '+' : '',
      };
    }

    const ratioMatch = raw.match(/^(\d+)\s*\/\s*(\d+)$/);

    if (ratioMatch) {
      return {
        type: 'ratio',
        raw,
        numerator: Number(ratioMatch[1]),
        denominator: Number(ratioMatch[2]),
      };
    }

    const numberMatch = raw.match(/^([+-]?)(\d[\d\s.,]*)$/);

    if (numberMatch) {
      const digits = numberMatch[2].replace(/\D/g, '');
      const unsignedValue = Number(digits);
      const value =
        numberMatch[1] === '-'
          ? -unsignedValue
          : unsignedValue;

      return {
        type: 'number',
        raw,
        value,
        prefix: numberMatch[1] === '+' ? '+' : '',
      };
    }

    return { type: 'text', raw };
  }

  function formatAuraNumber(value, prefix = '') {
    const rounded = Math.round(value);

    if (rounded < 0) {
      return '-' + Math.abs(rounded);
    }

    return prefix + rounded;
  }

  function setAuraText(element, value, suffix) {
    element.textContent = String(value) + suffix;
  }

  function finishAuraAnimation(element, state, target, suffix) {
    if (auraStates.get(element) !== state) return;

    state.raf = null;
    state.model = target;

    state.currentNumber =
      target.type === 'number'
        ? target.value
        : target.type === 'ratio'
          ? target.numerator
          : target.type === 'infinity'
            ? Number.POSITIVE_INFINITY
            : null;

    setAuraText(element, target.raw, suffix);
    element.classList.remove('is-aura-counting');
  }

  function runAuraAnimation(
    element,
    state,
    target,
    suffix,
    from,
    to,
    duration,
    render
  ) {
    const startedAt = performance.now();

    element.classList.remove('is-aura-counting');
    void element.offsetWidth;
    element.classList.add('is-aura-counting');

    const frame = (now) => {
      if (auraStates.get(element) !== state) return;

      const progress = Math.min(
        (now - startedAt) / duration,
        1
      );

      const eased = 1 - Math.pow(1 - progress, 4);
      const current = from + (to - from) * eased;

      state.currentNumber = current;
      setAuraText(element, render(current), suffix);

      if (progress < 1) {
        state.raf = window.requestAnimationFrame(frame);
      } else {
        finishAuraAnimation(
          element,
          state,
          target,
          suffix
        );
      }
    };

    state.raf = window.requestAnimationFrame(frame);
  }

  function getAuraStartingNumber(
    previousState,
    element,
    fromZero
  ) {
    if (fromZero) return 0;

    if (
      previousState &&
      Number.isFinite(previousState.currentNumber)
    ) {
      return previousState.currentNumber;
    }

    if (previousState?.model?.type === 'infinity') {
      return 99999;
    }

    if (previousState?.model?.type === 'ratio') {
      return previousState.model.numerator;
    }

    if (previousState?.model?.type === 'number') {
      return previousState.model.value;
    }

    const current = parseAura(element.textContent);

    if (current.type === 'number') {
      return current.value;
    }

    if (current.type === 'ratio') {
      return current.numerator;
    }

    if (current.type === 'infinity') {
      return 99999;
    }

    return 0;
  }

  function animateAuraElement(
    element,
    rawTarget,
    options = {}
  ) {
    if (!element) return;

    const target = parseAura(rawTarget);
    const suffix =
      options.suffix ??
      element.dataset.auraSuffix ??
      '';

    const previousState = auraStates.get(element);

    const from = getAuraStartingNumber(
      previousState,
      element,
      Boolean(options.fromZero)
    );

    if (previousState?.raf) {
      window.cancelAnimationFrame(previousState.raf);
    }

    if (auraObserver) {
      auraObserver.unobserve(element);
    }

    element.dataset.auraValue = target.raw;
    element.dataset.auraSuffix = suffix;
    element.dataset.farmeaAuraAnimated = 'true';

    element.setAttribute(
      'aria-label',
      (target.raw + suffix).trim()
    );

    const state = {
      model: previousState?.model || null,
      currentNumber: from,
      raf: null,
    };

    auraStates.set(element, state);

    if (
      prefersReducedMotion() ||
      target.type === 'text'
    ) {
      finishAuraAnimation(
        element,
        state,
        target,
        suffix
      );
      return;
    }

    if (target.type === 'number') {
      const distance = Math.abs(target.value - from);

      const duration = Math.min(
        1100,
        Math.max(
          650,
          650 + Math.log10(distance + 1) * 85
        )
      );

      runAuraAnimation(
        element,
        state,
        target,
        suffix,
        from,
        target.value,
        duration,
        (value) =>
          formatAuraNumber(value, target.prefix)
      );

      return;
    }

    if (target.type === 'ratio') {
      const ratioFrom =
        previousState?.model?.type === 'ratio' &&
        !options.fromZero
          ? from
          : 0;

      runAuraAnimation(
        element,
        state,
        target,
        suffix,
        ratioFrom,
        target.numerator,
        720,
        (value) =>
          Math.round(value) +
          '/' +
          target.denominator
      );

      return;
    }

    const infinityTarget = Math.max(
      99999,
      Math.abs(from) + 50000
    );

    runAuraAnimation(
      element,
      state,
      target,
      suffix,
      Number.isFinite(from) ? from : 0,
      infinityTarget,
      1050,
      (value) =>
        formatAuraNumber(value, target.prefix)
    );
  }

  function zeroAuraText(target) {
    if (target.type === 'ratio') {
      return '0/' + target.denominator;
    }

    if (
      target.type === 'number' ||
      target.type === 'infinity'
    ) {
      return (target.prefix || '') + '0';
    }

    return target.raw;
  }

  function getAuraObserver() {
    if (
      auraObserver ||
      !('IntersectionObserver' in window)
    ) {
      return auraObserver;
    }

    auraObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const element = entry.target;

          animateAuraElement(
            element,
            element.dataset.auraValue ||
              element.textContent,
            {
              fromZero: true,
              suffix:
                element.dataset.auraSuffix || '',
            }
          );
        });
      },
      {
        threshold: .28,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    return auraObserver;
  }

  function initAuraCounters(container = document) {
    const elements = [];

    if (
      container instanceof Element &&
      container.matches(AURA_SELECTOR)
    ) {
      elements.push(container);
    }

    elements.push(
      ...container.querySelectorAll(AURA_SELECTOR)
    );

    elements.forEach((element) => {
      if (
        element.dataset.farmeaAuraBound === 'true'
      ) {
        return;
      }

      element.dataset.farmeaAuraBound = 'true';

      const rawTarget =
        element.dataset.auraValue ||
        element.textContent;

      const target = parseAura(rawTarget);
      const suffix =
        element.dataset.auraSuffix || '';

      element.setAttribute(
        'aria-label',
        (target.raw + suffix).trim()
      );

      if (
        prefersReducedMotion() ||
        target.type === 'text'
      ) {
        setAuraText(
          element,
          target.raw,
          suffix
        );

        auraStates.set(element, {
          model: target,
          currentNumber: null,
          raf: null,
        });

        return;
      }

      setAuraText(
        element,
        zeroAuraText(target),
        suffix
      );

      auraStates.set(element, {
        model: null,
        currentNumber: 0,
        raf: null,
      });

      const observer = getAuraObserver();

      if (observer) {
        observer.observe(element);
      } else {
        animateAuraElement(
          element,
          target.raw,
          {
            fromZero: true,
            suffix,
          }
        );
      }
    });
  }

  function initFarmeaProduct(root) {
    if (
      !root ||
      root.dataset.farmeaBound === 'true'
    ) {
      return;
    }

    root.dataset.farmeaBound = 'true';

    const variantData = root.querySelector(
      '[data-farmea-variants]'
    );

    if (!variantData) return;

    let variants = [];

    try {
      variants = JSON.parse(
        variantData.textContent
      );
    } catch (error) {
      return;
    }

    const variantById = new Map(
      variants.map((variant) => [
        String(variant.id),
        variant,
      ])
    );

    const options = [
      ...root.querySelectorAll('[data-variant-id]'),
    ];

    const cards = [
      ...root.querySelectorAll('[data-select-card]'),
    ];

    const form = root.querySelector(
      '[data-product-form]'
    );

    const variantInput = root.querySelector(
      '[data-variant-input]'
    );

    const addButton = root.querySelector(
      '[data-add-button]'
    );

    const addLabel = root.querySelector(
      '[data-add-label]'
    );

    const mobileAdd = root.querySelector(
      '[data-mobile-add]'
    );

    const message = root.querySelector(
      '[data-form-message]'
    );

    const image = root.querySelector(
      '.fd-product-image'
    );

    const placeholder = root.querySelector(
      '[data-image-placeholder]'
    );

    const mobilePreviewImage = root.querySelector(
      '[data-mobile-preview-image]'
    );

    const mobilePreviewPlaceholder =
      root.querySelector(
        '[data-mobile-preview-placeholder]'
      );

    const lowStockThreshold = Number(
      root.dataset.lowStockThreshold || 8
    );

    function setText(selector, value) {
      const element = root.querySelector(selector);

      if (element) {
        element.textContent = value;
      }
    }

    function updateStock(variant) {
      const status = root.querySelector(
        '[data-stock-status]'
      );

      const count = root.querySelector(
        '[data-stock-count]'
      );

      const ring = root.querySelector(
        '[data-stock-ring]'
      );

      const ringNumber = root.querySelector(
        '[data-stock-ring-number]'
      );

      const track = root.querySelector(
        '[data-stock-track]'
      );

      const card = root.querySelector(
        '[data-stock-card]'
      );

      const stockMessage = root.querySelector(
        '[data-stock-message]'
      );

      if (
        !status ||
        !count ||
        !ring ||
        !ringNumber ||
        !track ||
        !card
      ) {
        return;
      }

      card.classList.remove(
        'is-low',
        'is-critical',
        'is-sold-out'
      );

      if (!variant.available) {
        status.textContent =
          'AGOTADA PARA SIEMPRE';

        count.innerHTML =
          'QUEDAN 0 <em>DE ' +
          variant.editionTotal +
          '</em>';

        ringNumber.textContent = '0';

        ring.style.setProperty(
          '--fd-stock',
          '0%'
        );

        track.style.width = '0%';
        card.classList.add('is-sold-out');

        if (stockMessage) {
          stockMessage.textContent =
            'RANGO CERRADO · Esta edición se agotó y no volverá a producirse.';
        }

        return;
      }

      if (!variant.inventoryTracked) {
        status.textContent = 'DISPONIBLE';

        count.innerHTML =
          'INVENTARIO <em>ACTIVO</em>';

        ringNumber.textContent = '✓';

        ring.style.setProperty(
          '--fd-stock',
          '100%'
        );

        track.style.width = '100%';

        if (stockMessage) {
          stockMessage.textContent =
            'EDICIÓN FUNDADORA · Disponible mientras este rango permanezca abierto.';
        }

        return;
      }

      const total = Math.max(
        Number(variant.editionTotal) || 1,
        1
      );

      const remaining = Math.max(
        Number(variant.remaining) || 0,
        0
      );

      const percentage = Math.min(
        (remaining / total) * 100,
        100
      );

      if (remaining === 1) {
        status.textContent =
          'ÚLTIMA Y ÚNICA DISPONIBLE';

        card.classList.add('is-critical');

        if (stockMessage) {
          stockMessage.textContent =
            'ÚLTIMA PIEZA · Cuando se venda, ' +
            variant.title +
            ' quedará cerrado para siempre.';
        }
      } else if (
        remaining <= lowStockThreshold
      ) {
        status.textContent =
          'SOLO QUEDAN ' + remaining;

        card.classList.add('is-low');

        if (stockMessage) {
          stockMessage.textContent =
            'SIN REPOSICIÓN · Cuando se agoten, no habrá más. Este rango se cerrará para siempre.';
        }
      } else {
        status.textContent =
          'INVENTARIO DEL DROP';

        if (stockMessage) {
          stockMessage.textContent =
            'SIN REPOSICIÓN · Cuando se agoten, no habrá más. Este rango se cerrará para siempre.';
        }
      }

      count.innerHTML =
        'QUEDAN ' +
        remaining +
        ' <em>DE ' +
        total +
        '</em>';

      ringNumber.textContent =
        String(remaining);

      ring.style.setProperty(
        '--fd-stock',
        percentage + '%'
      );

      track.style.width =
        percentage + '%';
    }

    function replayImageChange(imageElement) {
      imageElement.classList.remove(
        'is-variant-changing'
      );

      void imageElement.offsetWidth;

      imageElement.classList.add(
        'is-variant-changing'
      );

      window.setTimeout(() => {
        imageElement.classList.remove(
          'is-variant-changing'
        );
      }, 420);
    }

    function syncVariantImage(
      imageElement,
      placeholderElement,
      source,
      variant
    ) {
      if (
        !imageElement ||
        !placeholderElement
      ) {
        return;
      }

      if (source) {
        /*
         * Shopify genera un srcset para la
         * imagen principal. Hay que retirarlo
         * antes de cambiar src para evitar que
         * el navegador conserve la imagen
         * anterior.
         */
        imageElement.removeAttribute('srcset');
        imageElement.removeAttribute('sizes');

        imageElement.src = source;

        imageElement.alt =
          'Pulsera FARMEA ' + variant.title;

        imageElement.hidden = false;
        placeholderElement.hidden = true;

        replayImageChange(imageElement);
      } else {
        imageElement.removeAttribute('src');
        imageElement.removeAttribute('srcset');
        imageElement.removeAttribute('sizes');

        imageElement.alt = '';
        imageElement.hidden = true;
        placeholderElement.hidden = false;
      }
    }

    function updateImage(variant) {
      syncVariantImage(
        image,
        placeholder,
        variant.image,
        variant
      );

      syncVariantImage(
        mobilePreviewImage,
        mobilePreviewPlaceholder,
        variant.mobileImage ||
          variant.image,
        variant
      );

      setText(
        '[data-placeholder-rank]',
        variant.title
      );

      setText(
        '[data-mobile-preview-placeholder-rank]',
        variant.title
      );
    }

    function updateVariant(
      variant,
      shouldScroll = false
    ) {
      if (!variant) return;

      const index = variants.findIndex(
        (item) =>
          String(item.id) ===
          String(variant.id)
      );

      root.style.setProperty(
        '--fd-accent',
        variant.accent || '#9E6CFF'
      );

      options.forEach((option) => {
        const active =
          option.dataset.variantId ===
          String(variant.id);

        option.classList.toggle(
          'is-active',
          active
        );

        option.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );
      });

      setText(
        '[data-rank-number]',
        'RANGO ' +
          String(index + 1).padStart(2, '0') +
          '/07'
      );

      setText(
        '[data-edition-label]',
        'EDICIÓN ' + variant.editionTotal
      );

      setText(
        '[data-mobile-preview-number]',
        String(index + 1).padStart(2, '0') +
          '/07'
      );

      setText(
        '[data-mobile-preview-title]',
        variant.title
      );

      animateAuraElement(
        root.querySelector(
          '[data-mobile-preview-aura]'
        ),
        variant.aura
      );

      animateAuraElement(
        root.querySelector(
          '[data-media-aura]'
        ),
        variant.aura
      );

      setText(
        '[data-variant-title]',
        variant.title
      );

      animateAuraElement(
        root.querySelector(
          '[data-variant-aura]'
        ),
        variant.aura,
        {
          suffix: ' AURA',
        }
      );

      setText(
        '[data-variant-price]',
        variant.price
      );

      setText(
        '[data-variant-material]',
        variant.material
      );

      setText(
        '[data-variant-detail]',
        variant.detail
      );

      setText(
        '[data-mobile-title]',
        variant.title
      );

      animateAuraElement(
        root.querySelector(
          '[data-mobile-aura]'
        ),
        variant.aura
      );

      setText(
        '[data-mobile-price]',
        variant.price
      );

      const badge = root.querySelector(
        '[data-variant-badge]'
      );

      if (badge) {
        badge.textContent =
          variant.badge || '';

        badge.hidden =
          !variant.badge;
      }

      if (variantInput) {
        variantInput.value = variant.id;
      }

      updateImage(variant);
      updateStock(variant);

      if (addButton && addLabel) {
        addButton.disabled =
          !variant.available;

        addLabel.textContent =
          !variant.available
            ? 'AGOTADA PARA SIEMPRE'
            : variant.privateSale
              ? 'SOLICITAR LA PIEZA 1/1'
              : 'AÑADIR ' +
                variant.title +
                ' AL DROP';
      }

      if (mobileAdd) {
        mobileAdd.disabled =
          !variant.available;

        mobileAdd.textContent =
          !variant.available
            ? 'AGOTADA'
            : variant.privateSale
              ? 'SOLICITAR'
              : 'AÑADIR';
      }

      if (message) {
        message.textContent = '';
      }

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'variant',
        variant.id
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );

      if (shouldScroll) {
        root.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }

    options.forEach((option) => {
      option.addEventListener(
        'click',
        () => {
          updateVariant(
            variantById.get(
              option.dataset.variantId
            )
          );
        }
      );
    });

    cards.forEach((card) => {
      card.addEventListener(
        'click',
        () => {
          updateVariant(
            variantById.get(
              card.dataset.selectCard
            ),
            true
          );
        }
      );
    });

    if (mobileAdd && form) {
      mobileAdd.addEventListener(
        'click',
        () => form.requestSubmit()
      );
    }

    if (form) {
      form.addEventListener(
        'submit',
        async (event) => {
          event.preventDefault();

          if (
            !addButton ||
            addButton.disabled
          ) {
            return;
          }

          const originalLabel =
            addLabel
              ? addLabel.textContent
              : '';

          addButton.disabled = true;

          if (mobileAdd) {
            mobileAdd.disabled = true;
          }

          if (addLabel) {
            addLabel.textContent =
              'AÑADIENDO TU RANGO…';
          }

          if (message) {
            message.textContent = '';
          }

          try {
            const response = await fetch(
              (
                window.Shopify?.routes?.root ||
                '/'
              ) + 'cart/add.js',
              {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                },
                body: new FormData(form),
              }
            );

            const result =
              await response.json();

            if (!response.ok) {
              throw new Error(
                result.description ||
                  'No se pudo añadir la pieza.'
              );
            }

            if (
              root.dataset.redirectToCart ===
              'true'
            ) {
              window.location.assign(
                (
                  window.Shopify?.routes
                    ?.root || '/'
                ) + 'cart'
              );

              return;
            }

            if (message) {
              message.textContent =
                'Tu rango se ha añadido a la bolsa.';
            }

            if (addLabel) {
              addLabel.textContent =
                'AÑADIDA ✓';
            }

            window.setTimeout(() => {
              addButton.disabled = false;

              if (mobileAdd) {
                mobileAdd.disabled = false;
              }

              if (addLabel) {
                addLabel.textContent =
                  originalLabel;
              }
            }, 1600);
          } catch (error) {
            if (message) {
              message.textContent =
                error.message;
            }

            addButton.disabled = false;

            if (mobileAdd) {
              mobileAdd.disabled = false;
            }

            if (addLabel) {
              addLabel.textContent =
                originalLabel;
            }
          }
        }
      );
    }
  }

  function initAll(container = document) {
    initAuraCounters(container);

    container
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initFarmeaProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initAll(event.target)
  );
})();